import { Location } from '@angular/common';
import { ModalController } from '@ionic/angular';
import { ActivatedRoute } from '@angular/router';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';

import { CloseButtonComponent } from '@shared/components';
import { FoldersService } from '@modules/management/modules/folders/services/folders.service';

import { AddFolderParameters, FolderDataType, IFolder, IFolderCoreTarget } from './../../model/folders.interfaces';
import { BaseDetailsPage } from '@modules/management/pages/details/abstractDetails/abstractBaseDetails.page';
import {
  FormBuilderService,
  LoadingService,
  PopoverService,
  FoldersDataService,
  FormField,
  UtilsService,
  ChangeDetectionDeactivator,
  Module
} from '@services';

import { TranslateService } from '@ngx-translate/core';
import {
  assign,
  cloneDeep,
  each,
  filter,
  find, flatten,
  forEach,
  includes,
  isArray,
  map,
  mergeWith,
  some,
} from 'lodash';

@Component({
  selector: 'app-add-folder',
  templateUrl: './add-folder.page.html',
  styleUrls: ['./add-folder.page.scss'],
})
export class AddFolderComponent extends BaseDetailsPage implements OnInit {
  @ViewChild(CloseButtonComponent) closeButtonComponent: CloseButtonComponent;
  public title = this.translate.instant('MGMT_DETAILS.Add_Folder');
  public extraFieldsNames: string[];
  public parentData: IFolder;
  public coreTargets: IFolderCoreTarget[] = [];

  protected newForm: any = {
    id: 'folderAddForm',
    cancelButton: 'folderAddCancel',
    saveButton: 'folderAddSave'
  };
  protected editForm: any = {
    id: 'folderEditForm',
    cancelButton: 'folderEditCancel',
    saveButton: 'folderEditSave',
    deleteButton: 'folderEditDelete'
  };
  protected formConfig: any = {
    autocomplete: false,
    canClear: true,
    containers: true,
    prefix: 'folderAdd',
    del: null,
    save: null,
    cancel: this.translate.instant('SHARED.Cancel'),
    fields: [
      {
        required: true,
        inputtype: 'verbatim',
        name: 'title',
        title: this.translate.instant('MGMT_DETAILS.Folder_name'),
        type: 'text',
        size: 20,
        maxlength: 50
      },
      {
        required: false,
        inputtype: 'verbatim',
        name: 'translation_title',
        title: this.translate.instant('SHARED.Translations'),
        type: 'textTranslations',
        fromID: 'translations',
        size: 20,
        maxlength: 50
      }
    ]
  };
  private folderType: FolderDataType;
  private inputParameters: AddFolderParameters;

  constructor(
    protected route: ActivatedRoute,
    protected formBuilderService: FormBuilderService,
    protected elementRef: ElementRef,
    protected popoverService: PopoverService,
    protected location: Location,
    protected loadingService: LoadingService,
    protected translate: TranslateService,
    private foldersDataService: FoldersDataService,
    private foldersService: FoldersService,
    private utils: UtilsService,
    private modalController: ModalController,
    private changeDetectionDeactivator: ChangeDetectionDeactivator,
  ) {
    super(route, formBuilderService, elementRef, popoverService, location, loadingService, translate);
  }

  ngOnInit() {
    this.disableBackNavigation = true;
    this.folderType = this.inputParameters.folderType;
    this.initParentIDField();
    
    super.ngOnInit();
  }

  protected deleteHandler: any = (formData: any) => {
    if (this.messageID) {
      formData.folderID = this.messageID;
    }
    return this.foldersDataService.deleteFolder(formData);
  }

  protected updateHandler: any = async (formData: any) => {
    formData.folderID = +this.messageID;

    // Commit any core-target removals staged by CoreTargetsPickerComponent. The
    // staged survivor list arrives as formData.coreTargets (JSON string from the
    // hidden input); undefined means the field was untouched -> no removals.
    const survivors = this.parseCoreTargets(formData.coreTargets);
    const removed = filter(this.coreTargets, (o) => !some(survivors, (s) => s.nfcID === o.nfcID));
    // Delete one at a time, checking reqStatus, and shrink the baseline as each
    // succeeds. This keeps a retried Save correct — `removed` is recomputed
    // against what is actually gone, so already-deleted targets are never
    // re-deleted — and a failed delete aborts before updateFolder rather than
    // committing a partial removal alongside a folder update (QA round 1, F-02).
    for (const ct of removed) {
      const res: any = await this.foldersDataService.deleteCoreTarget(ct.nfcID);
      if (res?.reqStatus !== 'OK') {
        throw new Error(res?.errorMessage || `Failed to remove core target ${ct.nfcID}`);
      }
      this.coreTargets = filter(this.coreTargets, (o) => o.nfcID !== ct.nfcID);
    }

    // The backend owns coreTargets; never send them as folder attributes (it
    // preserves the surviving set on omit).
    delete formData.coreTargets;
    formData = this.prepareFormData(formData);
    if (formData.attributes) {
      delete formData.attributes.coreTargets;
    }

    return this.foldersDataService.updateFolder(formData);
  }

  private parseCoreTargets(value: any): IFolderCoreTarget[] {
    if (value === undefined || value === null) {
      return this.coreTargets;
    }
    if (isArray(value)) {
      return value;
    }
    try {
      return JSON.parse(value) || [];
    } catch (error) {
      return this.coreTargets;
    }
  }

  public isValid() {
    this.checkSaveDialogBeforeSend();
    return super.isValid();
  }

  private checkSaveDialogBeforeSend() {
    if (this.messageID) {
      let formData = this.formBuilderService.getFormData($(`#${this.editForm.id}`));
      formData = this.prepareFormData(cloneDeep(formData));
      this.updatePopoverConfig.description = this.translate.instant('MGMT_DETAILS.ABSTRACT_description0');

      this.foldersService.checkAndUpdateSaveDialog(formData, +this.messageID, this.updatePopoverConfig);
    }
  }

  protected onFinish(): void {
    this.foldersService.onFinishRenderAddFolderForm(this.formConfig);
  }

  protected addHandler: any = async (formData: any) => {
    formData.type = this.folderType;
    formData.parentID = this.inputParameters?.parentFolder ?? 0;
    formData = this.prepareFormData(formData);

    // For new folders, check conflicts only if they're being created under an existing parent
    // The folderID won't exist yet, so we check after creation (conflicts will resolve via inheritance)
    // No need to check for new folders as they won't have children yet

    return this.foldersDataService.addFolder(formData);
  }

  protected prepareFormConfig(): void {
    if (!this.messageID && this.inputParameters?.folderID) {
      this.messageID = this.inputParameters.folderID.toString();
    }

    if (this.messageID) {
      this.title = this.translate.instant('MGMT_DETAILS.Edit_Folder');
      this.formConfig.del = this.translate.instant('MGMT_DETAILS.Delete_Folder');
      this.updatePopoverConfig.title = this.translate.instant('MGMT_DETAILS.Update_Folder');
      this.deletePopoverConfig.title = this.translate.instant('SHARED.Are_You_Sure');
      this.deletePopoverConfig.description = this.translate.instant('SHARED.Asset_Type_Delete_Description');

      this.formConfig.prefix = 'folderEdit';
      this.formConfig.save = this.translate.instant('SHARED.Save_Changes');
      this.updatePopoverConfig.description = this.translate.instant('SHARED.Are_You_Sure');
    } else {
      this.title = this.translate.instant('MGMT_DETAILS.Add_Folder');
      this.formConfig.save = this.translate.instant('MGMT_DETAILS.Add_Folder');
    }

    const extraFields = this.foldersService.getAdditionFields(this.getData());
    if (extraFields?.length) {
      this.formConfig.fields = [...this.formConfig.fields, ...extraFields];
      this.extraFieldsNames = [];
      each(extraFields, (field) => {
        this.extraFieldsNames.push(field.name);
        if (field.type === 'timepicker') {
          this.extraFieldsNames.push(`${field.name}_unit`);
        }
      });
    }
  }

  public cancel() {
    if (this.isFormChanged()) {
      this.changeDetectionDeactivator.showAlert(() => {
        this.modalController.dismiss();
      });
    } else {
      this.modalController.dismiss();
    }
  }

  protected getData(): any {
    let folderData = {
      parentID: +this.inputParameters?.parentFolder || 0
    } as IFolder;

    if (this.messageID) {
      folderData = this.foldersDataService.getFolderByID(+this.messageID);
      // capture the enriched core targets before assign() mutations below
      this.coreTargets = cloneDeep(folderData.attributes?.coreTargets ?? []);
    }

    // there is a parent folder; get its information
    if (folderData.parentID) {
      this.parentData = this.foldersDataService.getFolderByID(this.inputParameters.parentFolder);
    }

    this.utils.decodeTranslations(folderData, ['translation_title'], ['title']);
    this.foldersService.filterData(folderData);

    if (!this.messageID) {
      // a new folder has its parentAttributes defined from its parent - a merger of the parentAttributes and attributes of the parent folder
      const parentData = cloneDeep(this.parentData);
      folderData.attributes = {};
      folderData.parentAttributes = mergeWith(parentData?.attributes, parentData?.parentAttributes, (attrValue, srcAttrValue) => {
        if (isArray(attrValue) || isArray(srcAttrValue)) {
          return [
            ...flatten([attrValue]),
            ...flatten([srcAttrValue])
          ];
        }
      });
    }

    // allow the factory to integrate any parent additional attributes
    this.foldersService.integrateParentAttributes(folderData, this.parentData);

    folderData = assign(folderData, folderData.attributes);

    return folderData;
  }

  private prepareFormData(formData: any) {
    formData.attributes = {};
    this.foldersService.parseFormData(formData);
    this.utils.encodeTranslations(formData, ['translation_title'], ['title']);

    if (this.extraFieldsNames?.length) {
      forEach(this.extraFieldsNames, (fieldName) => {
        formData.attributes[fieldName] = formData[fieldName];
      });
    }

    return formData;
  }

  private getAvailableFolders(): IFolder[] {
    const folderID = +this.inputParameters?.folderID;
    let folders = this.foldersDataService.getFoldersByType(this.folderType);
    if (folderID) {
      let parentIDs = [+folderID];
      let linkedFolders = [+folderID];
      while (parentIDs.length) {
        parentIDs = map(filter(
          this.foldersDataService.getFoldersByType(this.folderType), (folder: IFolder) => includes(parentIDs, folder.parentID)),
          'folderID',
        );
        linkedFolders = [...linkedFolders, ...parentIDs];
      }
      folders = filter(folders, (folder: IFolder) => !includes(linkedFolders, folder.folderID));
    }

    return folders;
  }

  private initParentIDField() {
    const parentIDField = find(this.formConfig.fields, { name: 'parentID' }) as FormField;
    if (parentIDField) {
      parentIDField.options = this.getAvailableFolders();
    }
  }

  protected onComplete() {
    this.cancel();
  }
}
